import { Eye, Printer } from 'react-feather';
import PageLayout from '../../../components/PageLayout';
import {
  formatDate,
  getItemById,
  getQueryString,
  hasExpired,
} from '../../../utils/common.util';
import useSWR from 'swr';
import { useState } from 'react';
import ReportsModal from './ReportsModal';
import {
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
} from 'carbon-components-react';
import { useDebounceState } from '../../../utils/useDebounceState';
import PrintOutHead from '../../../components/PrintOutHead';
import { PrintView } from '../../../components/PrintView/print-view.component';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import Combobox from '../../../components/combobox/Combobox';
import { useUrlState } from '../../../utils/useUrlState';
import ExportReportButton from '../../../components/ExportReportButton';

const initialData = {
  dailyBillingFeeId: '',
  dateFrom: '',
  dateTo: '',
  studentId: '',
};

export default function StudentStatementReport() {
  const [isOpen, setIsOpen] = useState('');

  const { data: d } = useSWR(`daily-billing/fees/list`);
  const feeTypes = d?.dailyBillingFee ?? [];

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: students, isLoading: isStudentsLoading } = useSWR(
    `student/list?status=Enrolled&${getQueryString({
      query: searchStudent,
    })}`,
  );

  const [query, setQuery] = useUrlState(initialData);
  const mainUrl = `daily-billing/generate-student-statement`;
  const { data, isLoading, error } = useSWR(
    query.studentId && query.dateFrom && query.dateTo
      ? `${mainUrl}?${getQueryString(query)}`
      : null,
  );

  const feeStatements = data?.response?.feeStatements ?? [];
  const student = data?.response?.student ?? {};
  const feeType = getItemById(feeTypes, query.dailyBillingFeeId);

  function isDisabled(item) {
    return hasExpired(item?.invoice?.gracePeriodEndDate);
  }

  const pageTitle = 'Student Statement';

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Daily Fees', to: '/daily-fees' },
        { label: pageTitle, active: true },
      ]}
      title={
        <div className="d-flex align-items-center" style={{ gap: '30px' }}>
          <h1 className="h-20">{pageTitle}</h1>{' '}
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
            style={{ gap: '4px' }}
            onClick={() => setIsOpen('reports')}
          >
            <Eye size="14" strokeWidth="2px" />
            <span>VIEW OTHER REPORTS</span>
          </button>
        </div>
      }
      isLoading={isLoading}
    >
      {/* FILTERS  */}
      <p className="mt-20 text-12">
        Select at least a student, a fee type, and start date filters to show
        report.
      </p>
      <div
        className="mt-10"
        style={{
          display: 'grid',
          gap: '20px',
          gridTemplateColumns: 'repeat(4, 1fr)',
          alignItems: 'center',
        }}
      >
        <Combobox
          options={students ?? []}
          label="Select a Student"
          customStyles={{ width: '300px' }}
          value={
            query.studentId
              ? students?.find((s) => s.id === Number(query.studentId))
              : null
          }
          onChange={(option) => {
            setQuery({ studentId: option ? option.id : '' });
            setSearchStudent('');
          }}
          onSearch={setSearchStudent} // The combobox now just updates the search term state
          onClear={() => {
            setQuery({ studentId: '' });
            setSearchStudent('');
          }}
          loading={isStudentsLoading} // Pass the loading state down
          renderSelected={(option) =>
            `${option.firstName} ${option.lastName} - ${option.classDescription} `
          }
          renderOption={(option) =>
            `${option.firstName} ${option.lastName} - ${option.classDescription} `
          }
          labelKey="fullName"
          valueKey="id"
          searchPlaceholder="Search students..."
          placeholder=""
        />
        <Select
          labelText="Fee Type"
          value={query.dailyBillingFeeId}
          onChange={(e) => setQuery({ dailyBillingFeeId: e.target.value })}
        >
          <SelectItem selected value="" text="All" />
          {feeTypes?.map((item) => (
            <SelectItem
              key={item.id}
              text={
                item.name +
                (isDisabled(item) && item?.invoice?.status
                  ? ` - [${item.invoice.status}]`
                  : '')
              }
              value={item.id}
              disabled={isDisabled(item)}
            />
          ))}
        </Select>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateFrom: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateFrom: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="dateFrom"
            labelText="Start Date"
            value={query.dateFrom}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateFrom: e.target.value })}
          />
        </DatePicker>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          // minDate={
          //   new Date(query.dateFrom ?? '').toISOString().split('T')[0]
          // }
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                dateTo: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ dateTo: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="end-date"
            id="end-date"
            name="dateTo"
            labelText="End Date"
            value={query.dateTo}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ dateTo: e.target.value })}
          />
        </DatePicker>
      </div>

      <div className="mt-20">
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {feeStatements?.length > 0 && (
          <>
            <PrintOutHead
              title={`Daily Fee ${pageTitle}`}
              headings={[
                {
                  label: 'Student',
                  value: `${student?.firstName || ''} ${student?.lastName || 'All'}`,
                },
                {
                  label: 'Class',
                  value: student?.classDescription || '-',
                },
                {
                  label: 'Student Id',
                  value: student?.studentId,
                },
                {
                  label: 'Fee Type',
                  value: feeType?.name ?? 'All',
                },
                {
                  label: 'Date Range',
                  value: query.dateFrom
                    ? `${formatDate(query.dateFrom)} - ${formatDate(
                        query.dateTo,
                      )} `
                    : '-',
                },
              ]}
            />
            <PrintView>
              <div style={{ overflow: 'auto' }}>
                {feeStatements.map((statement) => (
                  <FeeStatementTable
                    key={statement.feeType}
                    statement={statement}
                  />
                ))}
              </div>
            </PrintView>
          </>
        )}
        {feeStatements?.length === 0 && (
          <p className="text-14 mt-30 text-grey">No data available.</p>
        )}

        {feeStatements?.length > 0 && (
          <div className="mt-30 d-flex gap-20 align-items-center flex-end">
            <ExportReportButton
              apiUrl={`${mainUrl}/export?${getQueryString(query)}`}
              fileName={`Daily Fee ${pageTitle}.xlsx`}
            />
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        )}
      </div>

      <ReportsModal
        isOpen={isOpen === 'reports'}
        closeModal={() => setIsOpen('')}
      />
    </PageLayout>
  );
}

const FeeStatementTable = ({ statement }) => {
  return (
    <div className="mt-20">
      <p>{statement.feeType}</p>
      <table className="bx--data-table table-white">
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Transaction</th>
            <th>Billing (GH₵)</th>
            <th>Payment (GH₵)</th>
            <th>Balance (GH₵)</th>
          </tr>
        </thead>
        <tbody>
          {/* Opening Balance Row */}
          <tr>
            <td colSpan="5">Opening Balance</td>
            <td>{statement.openingBalance}</td>
          </tr>

          {/* Transaction Rows */}
          {statement.transactions.map((tx) => {
            return (
              <tr key={tx.id}>
                <td>{formatDate(tx.date)}</td>
                <td>{tx.description}</td>
                <td>{tx.transactionType}</td>
                <td>{tx.billingAmount}</td>
                <td>{tx.paymentAmount}</td>
                <td>{tx.balance}</td>
              </tr>
            );
          })}

          {/* Closing Balance Row */}
          <tr className="font-bold">
            <td colSpan="5">Closing Balance_{statement.feeType} Fees</td>
            <td>{statement.closingBalance}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
